import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { proposals as proposalsApi, projects as projectsApi, people as peopleApi } from '../api';

const ROUTE_LABELS: Record<string, string> = {
  dashboard: 'Dashboard',
  projects: 'Projects',
  people: 'Team',
  templates: 'Templates',
  proposals: 'Proposals',
  'fee-estimator': 'Fee Estimator',
  analytics: 'Analytics',
  'advanced-analytics': 'Advanced Analytics',
  'content-library': 'Content Library',
  'rfp-parser': 'RFP Parser',
  'go-no-go': 'Go / No-Go',
  tenders: 'Tender Monitor',
  certifications: 'Certifications',
  competitors: 'Competitors',
  achievements: 'Achievements',
  settings: 'Settings',
  integrations: 'Integrations',
  pricing: 'Pricing',
  build: 'Builder',
  'compliance-matrix': 'Compliance Matrix',
  'nz-govt-wizard': 'NZ Govt Wizard',
  'capability-statement': 'Capability Statement',
  intelligence: 'Intelligence',
  clients: 'Clients',
  impact: 'Impact Report',
  lessons: 'Lessons Learned',
  'resource-planner': 'Resource Planner',
  'audit-log': 'Audit Log',
  'ai-usage': 'AI Usage',
  billing: 'Billing',
  branding: 'Branding',
  api: 'API & Webhooks',
  'tender-sources': 'Tender Sources',
  help: 'Help & Docs',
};

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Module-level cache so navigating around doesn't re-fetch the same name.
const nameCache = new Map<string, string>();

async function resolveEntityName(parent: string, id: string): Promise<string> {
  const key = `${parent}:${id}`;
  const hit = nameCache.get(key);
  if (hit) return hit;
  try {
    let name = id.slice(0, 8);
    if (parent === 'proposals') {
      const r = await proposalsApi.get(id);
      name = (r.data as { title?: string }).title || name;
    } else if (parent === 'projects') {
      const r = await projectsApi.get(id);
      name = (r.data as { name?: string }).name || name;
    } else if (parent === 'people') {
      const r = await peopleApi.get(id);
      name = (r.data as { name?: string }).name || name;
    }
    nameCache.set(key, name);
    return name;
  } catch {
    return id.slice(0, 8);
  }
}

interface Crumb {
  label: string;
  path?: string;
}

export default function Breadcrumbs() {
  const location = useLocation();
  const [crumbs, setCrumbs] = useState<Crumb[]>([]);

  useEffect(() => {
    const segments = location.pathname.split('/').filter(Boolean);
    if (segments.length <= 1) {
      setCrumbs([]);
      return;
    }

    let cancelled = false;
    (async () => {
      const result: Crumb[] = [];
      for (let i = 0; i < segments.length; i++) {
        const seg = segments[i];
        const parent = segments[i - 1];
        const isLast = i === segments.length - 1;
        const path = '/' + segments.slice(0, i + 1).join('/');

        if (UUID_REGEX.test(seg) && parent && (parent === 'proposals' || parent === 'projects' || parent === 'people')) {
          const name = await resolveEntityName(parent, seg);
          result.push({ label: name, path: isLast ? undefined : path });
        } else if (ROUTE_LABELS[seg]) {
          result.push({ label: ROUTE_LABELS[seg], path: isLast ? undefined : path });
        } else {
          result.push({
            label: seg.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
            path: isLast ? undefined : path,
          });
        }
      }
      if (!cancelled) setCrumbs(result);
    })();

    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  if (crumbs.length === 0) return null;

  return (
    <nav className="breadcrumbs" aria-label="Breadcrumb">
      <Link to="/dashboard" className="breadcrumb-item" aria-label="Home">
        <Home size={14} aria-hidden="true" />
      </Link>
      {crumbs.map((c, i) => (
        <React.Fragment key={i}>
          <ChevronRight size={14} className="breadcrumb-separator" aria-hidden="true" />
          {c.path ? (
            <Link to={c.path} className="breadcrumb-item">{c.label}</Link>
          ) : (
            <span className="breadcrumb-item current" aria-current="page">{c.label}</span>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
}
